import { createSelector } from "reselect";
import { AppState } from "..";
import {
  selectUser,
  ebayIntegrationSelector,
  userIdSelector,
} from "../system/selector";
import type {
  EBayIntegration,
  EtsyIntegration,
  PoshmarkIntegration,
  AmazonIntegration,
  MercariIntegration,
  WhatnotIntegration,
  VintedIntegration,
  DepopIntegration,
} from "src/interfaces/plaidIntegration.interface";

const getPlaidIntegrationState = (state: AppState) => state.plaidIntegration;

export const integrationsSelector = createSelector(
  getPlaidIntegrationState,
  (state) => state.items
);

export const integrationByIdSelector = createSelector(
  [integrationsSelector, (_, id) => id],
  (integrations, id) => integrations.find((i) => i.id === id)
);

export const getPlaidIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) => state.items.filter((i) => i.type === "expense")
);

export const getEBayIntegrations = createSelector(
  [
    getPlaidIntegrationState,
    (state) => (selectUser(state) as any)?.ebayRefreshToken,
    ebayIntegrationSelector,
    userIdSelector,
    (state) => (selectUser(state) as any)?.ebayError,
    (state) => (selectUser(state) as any)?.ebayIntegrationInventory,
    (state) => (selectUser(state) as any)?.ebayIntegrationSalesIsRunning,
    (state) => (selectUser(state) as any)?.ebayIntegrationInventoryIsRunning,
  ],
  (
    state,
    oldRefreshToken,
    ebayIntegration,
    user,
    ebayError,
    ebayIntegrationInventory,
    ebayIntegrationSalesIsRunning,
    ebayIntegrationInventoryIsRunning
  ): EBayIntegration[] => {
    const ebayIntegrations: EBayIntegration[] = [];

    if (oldRefreshToken) {
      ebayIntegrations.push({
        id: "ebay",
        sync: ebayIntegration,
        type: "ebay",
        name: "eBay",
        username: "",
        user,
        error: ebayError,
        inventory: ebayIntegrationInventory,
        salesIsRunning: ebayIntegrationSalesIsRunning,
        inventoryIsRunning: ebayIntegrationInventoryIsRunning,
      });
    }

    return [
      ...ebayIntegrations,
      ...(state.items.filter(
        (i) => i.type === "ebay"
      ) as unknown as EBayIntegration[]),
    ];
  }
);

export const getPoshmarkIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter(
      (i) => i.type === "poshmark"
    ) as unknown as PoshmarkIntegration[]
);

export const getPoshmarkIntegration = createSelector(
  getPoshmarkIntegrations,
  (integrations) => integrations[0]
);

export const getAmazonIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter(
      (i) => i.type === "amazon"
    ) as unknown as AmazonIntegration[]
);

export const getMercariIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter(
      (i) => i.type === "mercari"
    ) as unknown as MercariIntegration[]
);

export const getEtsyIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter((i) => i.type === "etsy") as unknown as EtsyIntegration[]
);

export const getWhatnotIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter(
      (i) => i.type === "what-not"
    ) as unknown as WhatnotIntegration[]
);

export const getVintedIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter(
      (i) => i.type === "vinted"
    ) as unknown as VintedIntegration[]
);

export const getDepopIntegrations = createSelector(
  getPlaidIntegrationState,
  (state) =>
    state.items.filter(
      (i) => i.type === "depop"
    ) as unknown as DepopIntegration[]
);
